import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import {
  CalendarDays, Clock, Users2, Phone, ChevronRight, Search, HelpCircle,
  CheckCircle2, CalendarClock, XCircle, AlertTriangle, Activity as ActivityIcon,
  GitBranch, StickyNote, Target, Hourglass, Coffee,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { logAudit } from "@/lib/audit";
import { minDelay } from "@/lib/ux";
import { ErrorView, EmptyState } from "@/components/StateViews";
import SingleDatePicker from "@/components/SingleDatePicker";
import { useAuth } from "@/contexts/AuthContext";
import LeadDrawer from "@/components/LeadDrawer";

const fmtGap = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ""}`.trim() : `${m}m`);
const pad = (n) => String(n).padStart(2, "0");
const fireAudit = (body) => logAudit(body);

function eventBadge(ev) {
  if (ev.category === "planned" && ev.activityType === "Meeting") {
    if (ev.actionStatus === "done") return { label: "Meeting Done", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", Icon: CheckCircle2 };
    if (ev.actionStatus === "cancelled") return { label: "Meeting Cancelled", cls: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300", Icon: XCircle };
    return { label: "Scheduled Meeting", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300", Icon: CalendarClock };
  }
  if (ev.category === "planned") return { label: `Scheduled ${ev.activityType}`, cls: "bg-sky/70 text-navy", Icon: CalendarClock };
  const map = {
    meeting_completed: { label: "Meeting Done", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", Icon: CheckCircle2 },
    meeting_rescheduled: { label: "Rescheduled", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300", Icon: CalendarClock },
    meeting_cancelled: { label: "Meeting Cancelled", cls: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300", Icon: XCircle },
    stage_change: { label: "Stage changed", cls: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300", Icon: GitBranch },
    note_added: { label: "Note added", cls: "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300", Icon: StickyNote },
    followup_done: { label: "Follow-up done", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", Icon: CheckCircle2 },
    activity_completed: { label: "Activity done", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", Icon: CheckCircle2 },
    proposal_sent: { label: "Proposal sent", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300", Icon: Target },
    assignment: { label: "Lead assigned", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300", Icon: Users2 },
  };
  return map[ev.type] || { label: ev.title || "Action", cls: "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300", Icon: ActivityIcon };
}

function detailText(ev) {
  if (ev.category === "planned" && ev.activityType === "Meeting") {
    if (ev.actionStatus === "done") return ev.outcome ? `Outcome: ${ev.outcome}` : "Marked done";
    if (ev.actionStatus === "cancelled") return ev.extra ? `Reason: ${ev.extra}` : "Cancelled";
    if (ev.actionStatus === "no_action_recorded") return "No action recorded";
    return "Scheduled / upcoming";
  }
  if (ev.type === "stage_change") return `${ev.oldValue || "—"} → ${ev.newValue || "—"}`;
  if (ev.type === "meeting_completed" && ev.outcome) return `Outcome: ${ev.outcome}`;
  if (ev.type === "meeting_rescheduled") return ev.newValue ? `New time: ${ev.newValue}` : "Rescheduled";
  if (ev.note) return ev.note.length > 70 ? `${ev.note.slice(0, 70)}…` : ev.note;
  if (ev.newValue) return String(ev.newValue);
  return "";
}

function LeadCell({ lead, onClick }) {
  if (!lead) return <span className="text-ink/40">—</span>;
  return (
    <button onClick={() => onClick(lead)} data-testid={`lead-link-${lead.id}`} className="group text-left">
      <span className="block font-medium text-navy group-hover:text-loop">{lead.name}{lead.company && lead.company !== lead.name ? <span className="font-normal text-ink/45"> · {lead.company}</span> : ""}</span>
      {lead.phone && <span className="flex items-center gap-1 text-[11px] text-ink/50 group-hover:text-loop"><Phone className="h-2.5 w-2.5" />{lead.phone}</span>}
    </button>
  );
}

/* ===================== Bucketed activity-density timeline ===================== */
const PX_PER_HOUR = 124;
const BUCKET_MIN = 15;
const LEFT_PAD = 36;
const sevSeg = { light: "bg-ink/15", medium: "bg-amber-400/60", long: "bg-orange-500/70" };
const HELP_ITEMS = [
  ["dot-sm", "Small dot", "1 action in that 15-min bucket"],
  ["dot-md", "Medium dot", "2–4 actions in that bucket"],
  ["dot-lg", "Large dot", "5+ actions in that bucket"],
  ["count", "Number on dot", "Total actions recorded in the bucket"],
  ["meeting", "Blue dot", "Bucket contains a scheduled or completed meeting"],
  ["warn", "Amber ring", "Scheduled meeting with no action recorded"],
  ["sel", "Highlighted dot", "Currently open bucket — its actions branch out from the dot"],
  ["gap", "Muted line segment", "No AcquireLoop activity in that time gap"],
  ["click", "Click a dot", "Branches its actions inline (chronological); click empty space to collapse"],
];

function HelpPopover() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button onMouseEnter={() => setOpen(true)} onClick={() => setOpen((v) => !v)} aria-label="How to read this timeline"
        data-testid="timeline-help-btn" className="flex h-7 w-7 items-center justify-center rounded-full text-ink/45 hover:bg-mist hover:text-loop">
        <HelpCircle className="h-4 w-4" />
      </button>
      {open && (
        <div role="dialog" aria-label="Timeline guide" data-testid="timeline-help-popover"
          className="absolute right-0 top-8 z-50 w-72 rounded-xl border border-sky bg-white p-3 shadow-xl">
          <p className="mb-2 font-sora text-sm font-semibold text-navy">Timeline Guide</p>
          <ul className="space-y-1.5">
            {HELP_ITEMS.map(([k, t, d]) => (
              <li key={k} className="flex items-start gap-2 text-[11px]">
                <span className="mt-0.5 font-semibold text-navy">{t}:</span>
                <span className="text-ink/60">{d}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 border-t border-sky/60 pt-2 text-[10px] text-ink/45">Only key activity clusters appear here. Full action history is in the detailed log below.</p>
        </div>
      )}
    </div>
  );
}

const fmtRange = (a, b) => `${a} – ${b}`;

function Timeline({ events, gaps, date, onLead }) {
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    const valid = events.filter((e) => e.time);
    if (!valid.length) return null;
    // Parse IST wall-clock minutes straight from the +05:30 ISO string (browser-tz independent).
    const wm = (iso) => { const m = String(iso).match(/T(\d{2}):(\d{2})/); return m ? (+m[1]) * 60 + (+m[2]) : 0; };
    const fmtTotal = (tm) => { let h = Math.floor(tm / 60) % 24, mi = tm % 60; const ap = h < 12 ? "AM" : "PM"; h = h % 12 || 12; return `${h}:${pad(mi)} ${ap}`; };
    const allMin = valid.map((e) => wm(e.time));
    const startHour = Math.min(9, Math.floor(Math.min(...allMin) / 60));
    const endHour = Math.max(20, Math.ceil(Math.max(...allMin) / 60));
    const startMin = startHour * 60;
    const totalH = endHour - startHour;
    const width = totalH * PX_PER_HOUR + LEFT_PAD * 2;
    const xOf = (tm) => ((tm - startMin) / 60) * PX_PER_HOUR + LEFT_PAD;

    const map = new Map();
    for (const e of valid) {
      const tm = wm(e.time);
      const idx = Math.floor((tm - startMin) / BUCKET_MIN);
      const key = `b-${idx}`;
      if (!map.has(key)) map.set(key, { key, startTotal: startMin + idx * BUCKET_MIN, events: [] });
      map.get(key).events.push(e);
    }
    const buckets = [...map.values()].sort((a, b) => a.startTotal - b.startTotal).map((bk) => {
      const evs = bk.events.sort((a, b) => wm(a.time) - wm(b.time));
      const leadIds = new Set(evs.filter((e) => e.lead).map((e) => e.lead.id));
      const hasMeeting = evs.some((e) => e.activityType === "Meeting" || String(e.type).startsWith("meeting"));
      const hasNoActionMeeting = evs.some((e) => e.category === "planned" && e.actionStatus === "no_action_recorded");
      return {
        ...bk, events: evs, count: evs.length, leadCount: leadIds.size, hasMeeting, hasNoActionMeeting,
        displayRange: fmtRange(fmtTotal(bk.startTotal), fmtTotal(bk.startTotal + BUCKET_MIN)),
        x: xOf(bk.startTotal + BUCKET_MIN / 2),
      };
    });
    const ticks = Array.from({ length: totalH + 1 }, (_, i) => ({ hour: startHour + i, x: i * PX_PER_HOUR + LEFT_PAD }));
    return { buckets, width, ticks, gapX: (iso) => xOf(wm(iso)) };
  }, [events, date]);

  const wrapRef = useRef(null);
  useEffect(() => {
    setSelected((s) => (s && model?.buckets?.some((b) => b.key === s) ? s : null));
  }, [model]);
  useEffect(() => {
    if (!selected) return;
    const onDown = (e) => { if (!e.target.closest("[data-branch-keep]")) setSelected(null); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [selected]);

  if (!model) return <div className="py-10 text-center text-sm text-ink/45">No key actions recorded on this day.</div>;
  const { buckets, width, ticks, gapX } = model;
  const sel = buckets.find((b) => b.key === selected);

  const dotSize = (c) => (c >= 5 ? "h-5 w-5 text-[10px]" : c >= 2 ? "h-4 w-4 text-[9px]" : "h-3 w-3 text-0");
  const dotColor = (b) => (b.hasNoActionMeeting ? "bg-orange-400" : b.hasMeeting ? "bg-blue-500" : "bg-emerald-500");

  // ---------- in-place vertical branch layout ----------
  const BASELINE = 92;          // y of the horizontal line (leaves room above for the hover tooltip)
  const ROW_H = 88;             // vertical spacing between branch cards
  const BRANCH_TOP = BASELINE + 20;
  const CONN_Y = 22;            // connector y-offset within a card row
  const CARD_W = 248;
  const CONN_W = 30;
  const BASE_H = BASELINE + 48;  // canvas height when no bucket is open
  const evs = sel?.events || [];
  // dot near right edge -> cards on the left; near left edge -> cards on the right; middle -> alternate
  const region = sel ? (sel.x > width * 0.6 ? "left" : sel.x < width * 0.4 ? "right" : "mid") : "mid";
  const sideOf = (i) => (region === "left" ? "left" : region === "right" ? "right" : i % 2 === 0 ? "right" : "left");
  const lineH = sel ? BRANCH_TOP + (evs.length - 1) * ROW_H + CONN_Y - BASELINE : 0;
  const canvasH = sel ? Math.max(BASE_H, BRANCH_TOP + (evs.length - 1) * ROW_H + 112) : BASE_H;

  return (
    <div data-testid="timeline-buckets" ref={wrapRef}>
      <div className="overflow-x-auto px-1 pt-3 pb-5">
        <div className="relative transition-[height] duration-300 ease-out" style={{ width, height: canvasH }}>
          {/* horizontal baseline */}
          <div className="absolute h-px bg-sky" style={{ left: 0, right: 0, top: BASELINE }} />
          {/* hour ticks + labels (above the line, single line) */}
          {ticks.map((t) => (
            <div key={t.hour} className="absolute flex -translate-x-1/2 flex-col items-center" style={{ left: t.x, top: BASELINE - 30 }}>
              <span className="whitespace-nowrap text-[10px] font-semibold tracking-tight text-ink/40">{t.hour % 12 || 12}{t.hour < 12 ? "AM" : "PM"}</span>
              <div className="mt-1 h-2.5 w-px bg-sky/70" />
            </div>
          ))}
          {/* activity gaps on the baseline */}
          {gaps.map((g, i) => {
            const x1 = gapX(g.from), x2 = gapX(g.to);
            return <div key={i} title={`No system activity · ${fmtGap(g.minutes)}`} data-testid={`gap-${i}`}
              className={`absolute rounded-full ${sevSeg[g.severity]}`} style={{ left: x1, width: Math.max(4, x2 - x1), height: 3, top: BASELINE - 1 }} />;
          })}

          {/* vertical dotted branch growing from the selected dot */}
          {sel && (
            <div className="pointer-events-none absolute border-l-2 border-dashed border-loop/45" style={{ left: sel.x, top: BASELINE, height: lineH }} />
          )}

          {/* dots */}
          {buckets.map((b) => {
            const isSel = b.key === selected;
            return (
              <div key={b.key}>
                <button
                  data-testid={`bucket-dot-${b.key}`}
                  data-branch-keep
                  onMouseEnter={() => setHover(b.key)} onMouseLeave={() => setHover(null)}
                  onClick={() => setSelected((s) => (s === b.key ? null : b.key))}
                  style={{ left: b.x, top: BASELINE }}
                  className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full font-bold text-white transition-all ${dotSize(b.count)} ${dotColor(b)} ${b.hasNoActionMeeting ? "ring-2 ring-orange-400/50" : ""} ${isSel ? "outline outline-2 outline-offset-2 outline-loop scale-110 z-30" : "hover:scale-110"}`}>
                  {b.count >= 2 ? b.count : ""}
                </button>
                {hover === b.key && !isSel && (
                  <div className="pointer-events-none absolute z-40 w-52 -translate-x-1/2 rounded-lg border border-sky bg-white p-2.5 text-left shadow-xl" style={{ left: b.x, top: BASELINE - 80 }} data-testid={`bucket-tip-${b.key}`}>
                    <p className="text-[11px] font-semibold text-navy">{b.displayRange}</p>
                    <p className="text-[10px] text-ink/55">{b.count} action{b.count === 1 ? "" : "s"} · {b.leadCount} lead{b.leadCount === 1 ? "" : "s"}</p>
                    {b.hasNoActionMeeting && <p className="mt-0.5 text-[10px] font-medium text-orange-500">Includes meeting with no action recorded</p>}
                  </div>
                )}
              </div>
            );
          })}

          {/* event cards alternating along the vertical branch (chronological top→bottom) */}
          {sel && evs.map((ev, i) => {
            const side = sideOf(i);
            const rowTop = BRANCH_TOP + i * ROW_H;
            const cardLeft = side === "right" ? sel.x + CONN_W : sel.x - CONN_W - CARD_W;
            const connLeft = side === "right" ? sel.x : sel.x - CONN_W;
            const bdg = eventBadge(ev);
            return (
              <div key={ev.id} data-branch-keep data-testid={`bucket-event-${ev.id}`}>
                <span className={`absolute z-20 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white ${ev.category === "planned" ? "bg-blue-500" : "bg-emerald-500"}`} style={{ left: sel.x, top: rowTop + CONN_Y }} />
                <div className="absolute border-t-2 border-dotted border-loop/40" style={{ left: connLeft, top: rowTop + CONN_Y, width: CONN_W }} />
                <div className="absolute z-10 rounded-xl border border-sky bg-white p-2.5 shadow-sm transition-shadow hover:shadow-md"
                  style={{ left: cardLeft, top: rowTop, width: CARD_W, animation: "stageIn .28s ease both", animationDelay: `${i * 45}ms` }}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-ink/60">{ev.displayTime}</span>
                    <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold ${bdg.cls}`}><bdg.Icon className="h-2.5 w-2.5" />{bdg.label}</span>
                  </div>
                  {ev.lead ? (
                    <button onClick={() => onLead(ev.lead)} data-testid={`bucket-lead-${ev.lead.id}`} className="group mt-1 block text-left">
                      <span className="block text-xs font-semibold text-navy group-hover:text-loop">{ev.lead.name}{ev.lead.company && ev.lead.company !== ev.lead.name ? <span className="font-normal text-ink/45"> · {ev.lead.company}</span> : ""}</span>
                      {ev.lead.phone && <span className="flex items-center gap-1 text-[10px] text-ink/45 group-hover:text-loop"><Phone className="h-2.5 w-2.5" />{ev.lead.phone}</span>}
                    </button>
                  ) : null}
                  {(detailText(ev) || (ev.category === "planned" && ev.actionStatus === "no_action_recorded")) && (
                    <p className={`mt-0.5 text-[10px] ${ev.actionStatus === "no_action_recorded" ? "font-medium text-orange-500" : "text-ink/55"}`}>{detailText(ev)}</p>
                  )}
                  {ev.lead && <button onClick={() => onLead(ev.lead)} className="mt-1 inline-flex items-center gap-0.5 text-[10px] font-semibold text-loop hover:underline">Open lead<ChevronRight className="h-3 w-3" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {sel && (
        <p className="mt-1 text-center text-[11px] text-ink/45" data-testid="branch-caption">
          {sel.displayRange} · {evs.length} action{evs.length === 1 ? "" : "s"} · click elsewhere to collapse
        </p>
      )}
    </div>
  );
}

/* ================================ Page ================================ */
function TimesheetSkeleton() {
  const sweep = { animation: "tsScan 1.8s ease-in-out infinite" };
  return (
    <div className="space-y-4" data-testid="timesheet-skeleton">
      {/* summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-sky/70 bg-white p-3.5">
            <div className="ts-shimmer mb-2 h-7 w-7 rounded-lg bg-mist" />
            <div className="ts-shimmer mb-1.5 h-6 w-14 rounded bg-mist" />
            <div className="ts-shimmer h-3 w-20 rounded bg-mist" />
          </div>
        ))}
      </div>
      {/* timeline */}
      <div className="rounded-2xl border border-sky/70 bg-white p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="space-y-1.5">
            <div className="ts-shimmer h-4 w-28 rounded bg-mist" />
            <div className="ts-shimmer h-3 w-56 rounded bg-mist" />
          </div>
          <div className="ts-shimmer h-7 w-24 rounded-lg bg-mist" />
        </div>
        <div className="relative h-24 overflow-hidden">
          {/* baseline */}
          <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-sky" />
          {/* pulsing dots */}
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="absolute top-1/2 h-3 w-3 rounded-full bg-loop/60"
              style={{ left: `${4 + i * 7}%`, animation: `tsDotPulse 1.4s ease-in-out ${i * 0.09}s infinite` }} />
          ))}
          {/* scanning highlight */}
          <span className="pointer-events-none absolute top-0 h-full w-24 -translate-x-1/2 bg-gradient-to-r from-transparent via-loop/15 to-transparent" style={sweep} />
        </div>
        <p className="mt-3 text-center text-xs font-medium text-loop">
          <span className="inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 animate-ping rounded-full bg-loop" />
            Building the day timeline…
          </span>
        </p>
      </div>
    </div>
  );
}

export default function TimesheetsReport() {
  const { users } = useOutletContext();
  const { has } = useAuth();
  const istToday = useMemo(() => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }), []);
  const [people, setPeople] = useState([]);
  const [userId, setUserId] = useState("");
  const [date, setDate] = useState(istToday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [logSearch, setLogSearch] = useState("");
  const [logType, setLogType] = useState("");
  const [logScope, setLogScope] = useState("all");

  useEffect(() => {
    api.get("/team/timesheets/users").then(({ data }) => {
      setPeople(data.data.users);
      if (data.data.users[0]) setUserId(data.data.users[0].id);
    }).catch((e) => setError(apiError(e)));
    fireAudit({ action_type: "report_opened", entity_type: "report", description: "Opened Timesheets" });
  }, []);

  const load = useCallback(async () => {
    if (!userId || !date) return;
    setLoading(true); setError("");
    try { const { data } = await minDelay(api.get("/team/timesheets", { params: { userId, date } })); setData(data.data); }
    catch (e) { setError(apiError(e)); setData(null); }
    finally { setLoading(false); }
  }, [userId, date]);
  useEffect(() => { load(); }, [load]);

  const openLead = (lead) => {
    setSelectedLead(lead.id);
    fireAudit({ action_type: "lead_drawer_opened", entity_type: "lead", lead_id: lead.id, description: `Opened ${lead.name} from Timesheets` });
  };

  if (!has("timesheets.view")) return <EmptyState title="You don't have access to timesheets." />;
  if (error && !data) return <ErrorView message={error} onRetry={load} />;

  const k = data?.summary, wb = data?.workBreakdown;
  const keyEvents = data?.keyTimelineEvents || [];
  const auditLog = data?.detailedAuditLog || [];
  const gaps = data?.gaps || [];

  const logTypes = [...new Set(auditLog.map((e) => e.type))].sort();
  const filteredLog = auditLog.filter((e) => {
    if (logScope === "lead" && !e.lead) return false;
    if (logScope === "system" && e.lead) return false;
    if (logType && e.type !== logType) return false;
    if (logSearch) {
      const q = logSearch.toLowerCase();
      const hay = `${e.lead?.name || ""} ${e.lead?.phone || ""} ${e.title || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const cards = !k ? [] : [
    { label: "Scheduled Meetings", value: k.scheduledMeetings, icon: CalendarClock, tone: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-500/15" },
    { label: "Meetings Done", value: k.meetingsDone, icon: CheckCircle2, tone: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-500/15" },
    { label: "Rescheduled", value: k.meetingsRescheduled, icon: CalendarClock, tone: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-500/15" },
    { label: "Cancelled", value: k.meetingsCancelled, icon: XCircle, tone: "text-red-600", bg: "bg-red-50 dark:bg-red-500/15" },
    { label: "No Action Recorded", value: k.meetingsWithNoAction, icon: AlertTriangle, tone: "text-orange-600", bg: "bg-orange-50 dark:bg-orange-500/15" },
    { label: "Tracked Actions", value: k.totalTrackedActions, icon: ActivityIcon, tone: "text-loop", bg: "bg-sky/50" },
    { label: "Leads Touched", value: k.leadsTouched, icon: Users2, tone: "text-navy", bg: "bg-mist" },
    { label: "Completed Activities", value: k.completedActivities, icon: CheckCircle2, tone: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-500/15" },
    { label: "Pending Activities", value: k.pendingScheduledActivities, icon: Hourglass, tone: "text-purple-600", bg: "bg-purple-50 dark:bg-purple-500/15" },
    { label: "First Activity", value: k.firstActivityAt || "—", icon: Clock, tone: "text-navy", bg: "bg-mist", small: true },
    { label: "Last Activity", value: k.lastActivityAt || "—", icon: Clock, tone: "text-navy", bg: "bg-mist", small: true },
    { label: "Longest Quiet Gap", value: k.longestGapMinutes ? fmtGap(k.longestGapMinutes) : "—", icon: Coffee, tone: "text-ink/70", bg: "bg-mist", small: true },
  ];

  const wbRows = !wb ? [] : [
    ["Meetings", [["Scheduled", wb.meetingsScheduled], ["Done", wb.meetingsDone], ["Rescheduled", wb.meetingsRescheduled], ["Cancelled", wb.meetingsCancelled], ["No action", wb.meetingsNoAction]]],
    ["Calls & Follow-ups", [["Calls scheduled", wb.callsScheduled], ["Calls completed", wb.callsCompleted], ["Follow-ups scheduled", wb.followupsScheduled], ["Follow-ups completed", wb.followupsCompleted]]],
    ["Lead work", [["Stage changes", wb.stageChanges], ["Notes added", wb.notesAdded], ["Leads updated", wb.leadsUpdated], ["New leads", wb.newLeadsTouched], ["Existing leads", wb.oldLeadsTouched]]],
  ];

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      <div>
        <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><CalendarDays className="h-7 w-7 text-loop" />Timesheets</h1>
        <p className="text-sm text-ink/60">Planned vs actual daily work — auto-generated from system activity.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-sky/70 bg-white p-3" data-testid="timesheet-filters">
        <div className="flex items-center gap-2">
          <Users2 className="h-4 w-4 text-ink/40" />
          <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="timesheet-user-select" className="h-9 min-w-[200px] rounded-lg border border-sky bg-white px-2 text-sm text-navy outline-none focus:border-loop">
            {people.length === 0 && <option value="">No team members</option>}
            {people.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role === "Sales Manager" ? "Manager" : "Exec"}</option>)}
          </select>
        </div>
        <SingleDatePicker value={date} max={istToday} onChange={setDate} testid="timesheet-date" />
        {data?.user && <span className="ml-auto text-xs text-ink/55">{data.user.name} · {data.user.role}{data.user.manager ? ` · reports to ${data.user.manager}` : ""}</span>}
      </div>

      {!userId ? (
        <EmptyState title="Select a team member to view their timesheet." />
      ) : loading ? (
        <TimesheetSkeleton />
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" data-testid="timesheet-summary">
            {cards.map((t) => (
              <div key={t.label} className="rounded-2xl border border-sky/70 bg-white p-3.5">
                <div className={`mb-2 inline-flex rounded-lg p-1.5 ${t.bg}`}><t.icon className={`h-4 w-4 ${t.tone}`} /></div>
                <p className={`font-sora font-bold text-navy ${t.small ? "text-lg" : "text-2xl"}`}>{t.value}</p>
                <p className="text-[11px] text-ink/55">{t.label}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid="timesheet-timeline">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <h3 className="font-sora text-sm font-semibold text-navy">Day Timeline</h3>
                <p className="text-[11px] text-ink/45">Each dot = a 15-min activity bucket · click a dot to see its actions</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-3 text-[11px] text-ink/55">
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />Planned / Meeting</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Actions</span>
                </div>
                <HelpPopover />
              </div>
            </div>
            <Timeline events={keyEvents} gaps={gaps} date={date} onLead={openLead} />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="timesheet-breakdown">
            {wbRows.map(([title, rows]) => (
              <div key={title} className="rounded-2xl border border-sky/70 bg-white p-4">
                <h3 className="mb-3 font-sora text-sm font-semibold text-navy">{title}</h3>
                <div className="space-y-2">
                  {rows.map(([label, val]) => (
                    <div key={label} className="flex items-center justify-between text-sm">
                      <span className="text-ink/60">{label}</span>
                      <span className="font-semibold text-navy">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {data.canViewAuditLog === false ? null : (
          <div className="rounded-2xl border border-sky/70 bg-white" data-testid="timesheet-actionlog">
            <div className="flex flex-wrap items-center gap-2 border-b border-sky/60 p-4">
              <h3 className="mr-auto font-sora text-sm font-semibold text-navy">Detailed Activity Log</h3>
              <div className="flex items-center gap-1.5 rounded-lg border border-sky px-2">
                <Search className="h-3.5 w-3.5 text-ink/40" />
                <input value={logSearch} onChange={(e) => setLogSearch(e.target.value)} placeholder="Lead or phone…" data-testid="log-search" className="h-8 w-36 bg-transparent text-xs text-navy outline-none" />
              </div>
              <select value={logType} onChange={(e) => setLogType(e.target.value)} data-testid="log-type-filter" className="h-8 rounded-lg border border-sky bg-white px-2 text-xs text-navy outline-none focus:border-loop">
                <option value="">All actions</option>
                {logTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
              <div className="flex rounded-lg border border-sky p-0.5 text-xs" data-testid="log-scope">
                {[["all", "All"], ["lead", "Lead"], ["system", "System/UI"]].map(([v, l]) => (
                  <button key={v} onClick={() => setLogScope(v)} className={`rounded-md px-2 py-1 font-medium transition-colors ${logScope === v ? "bg-loop text-white" : "text-ink/55 hover:bg-mist"}`}>{l}</button>
                ))}
              </div>
            </div>
            {filteredLog.length === 0 ? <EmptyState title="No actions match these filters." /> : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-mist/70 text-left text-ink/50">
                    <tr>
                      <th className="px-4 py-2.5 font-semibold">Time</th>
                      <th className="px-4 py-2.5 font-semibold">Action</th>
                      <th className="px-4 py-2.5 font-semibold">Lead</th>
                      <th className="px-4 py-2.5 font-semibold">Entity</th>
                      <th className="px-4 py-2.5 font-semibold">Details</th>
                      <th className="px-4 py-2.5 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sky/40">
                    {filteredLog.map((e) => {
                      const b = eventBadge(e);
                      return (
                        <tr key={`log-${e.id}`} className="hover:bg-sky/10" data-testid={`log-row-${e.id}`}>
                          <td className="whitespace-nowrap px-4 py-2.5 text-ink/60">{e.displayTime || "—"}</td>
                          <td className="whitespace-nowrap px-4 py-2.5"><span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${b.cls}`}><b.Icon className="h-2.5 w-2.5" />{b.label}</span></td>
                          <td className="px-4 py-2.5"><LeadCell lead={e.lead} onClick={openLead} /></td>
                          <td className="whitespace-nowrap px-4 py-2.5 capitalize text-ink/55">{e.entityType || "—"}</td>
                          <td className="px-4 py-2.5 text-ink/65">{detailText(e) || "—"}</td>
                          <td className="whitespace-nowrap px-4 py-2.5"><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${e.source === "ui" ? "bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-300" : "bg-mist text-ink/55"}`}>{e.source || "system"}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}
        </>
      )}

      <LeadDrawer leadId={selectedLead} open={!!selectedLead} onOpenChange={(v) => !v && setSelectedLead(null)} onUpdated={load} users={users} />
    </div>
  );
}
